import { X } from 'lucide-react';
import React, { useEffect, useRef } from 'react';
import { useDialogLab } from '../../lab/hooks/useDialogLab';

export type ModalSize = 'sm' | 'default' | 'md' | 'lg' | 'xl' | 'wide' | '2xl' | 'full';

/** Props do componente Modal V2 */
export interface ModalProps {
  /** Se o modal está visível */
  open: boolean;
  /** Callback acionado ao solicitar o fechamento */
  onClose: () => void;
  /** Título principal (string ou ReactNode para composições ricas) */
  title?: React.ReactNode;
  /** Subtítulo descritivo ou badge informativo abaixo do título (não sofre compressão) */
  subtitle?: React.ReactNode;
  /** Elementos extras no canto direito do cabeçalho (ex: badges, switchers) */
  headerExtra?: React.ReactNode;
  /** Conteúdo interno com scroll independente */
  children: React.ReactNode;
  /** Rodapé com botões de ação fixos na base */
  footer?: React.ReactNode;
  /** Conteúdo opcional à esquerda do rodapé (ex: botão 'Excluir' ou indicador de status) */
  footerLeft?: React.ReactNode;
  /**
   * Tamanho do modal:
   * - sm: 420px (Confirmações, alertas)
   * - default / md: 540px (Formulários simples)
   * - lg: 720px (Formulários médios)
   * - xl: 880px (Grids de 2 colunas)
   * - wide / 2xl: 1120px (Layouts duplos, fichas escolares)
   * - full: 1380px / 96vw (Relatórios, estúdio ECAD, grids massivas de notas)
   */
  size?: ModalSize;
  /** Classe extra no container (ex.: modal mais largo ou corpo sem padding) */
  contentClassName?: string;
  /** Se true, desativa o scroll interno do body (para layouts com scroll interno próprio) */
  noBodyScroll?: boolean;
  /** Se true, remove o padding padrão do body */
  flushBody?: boolean;
  /**
   * Quando true, impede fechamento por overlay click, ESC ou botao X.
   * Em vez disso, chama onAttemptClose (que pode mostrar confirmacao).
   * Se onAttemptClose nao for fornecido, simplesmente bloqueia o fechamento.
   */
  preventClose?: boolean;
  /** Chamado quando usuario tenta fechar e preventClose=true */
  onAttemptClose?: () => void;
  /** Se true, oculta o botao X de fechar no cabecalho */
  hideCloseButton?: boolean;
  /** Se true, permite fechar o modal clicando no fundo transparente (overlay). Default: false. */
  closeOnOverlayClick?: boolean;
  /** Nível de profundidade para modais aninhados (sobrescreve o stacking automático) */
  depth?: number;
  /**
   * ID do diálogo para integração com o sistema Lab de Roteiros Contextuais.
   * Quando fornecido, o Lab detecta automaticamente o contexto e sugere o roteiro vinculado.
   * Use o mesmo ID definido no LAB_DIALOG_MAP (ex: 'modal-novo-aluno').
   */
  dialogId?: string;
}

// Controle global de modais ativos para gerenciamento seguro de scroll do body (Nested Modals)
let globalOpenModalsCount = 0;

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  headerExtra,
  children,
  footer,
  footerLeft,
  size = 'default',
  contentClassName,
  noBodyScroll = false,
  flushBody = false,
  preventClose,
  onAttemptClose,
  hideCloseButton,
  closeOnOverlayClick = false,
  depth,
  dialogId,
}: ModalProps) {
  // Integração com o sistema Lab de Roteiros Contextuais
  useDialogLab(dialogId ?? null, open);
  const handleCloseAttempt = () => {
    if (preventClose) {
      onAttemptClose?.();
      return;
    }
    onClose();
  };

  const wasOpenRef = useRef(open);

  useEffect(() => {
    if (wasOpenRef.current && !open) {
      // Quando qualquer modal fecha, dispara atualização silenciosa de dados sem F5 / refresh
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('ac-data-refetch'));
      }
    }
    wasOpenRef.current = open;
  }, [open]);

  useEffect(() => {
    if (open) {
      globalOpenModalsCount++;
      if (globalOpenModalsCount === 1) {
        document.body.style.overflow = 'hidden';
      }

      const handleEsc = (e: KeyboardEvent) => {
        if (e.key === 'Escape') handleCloseAttempt();
      };
      window.addEventListener('keydown', handleEsc);

      return () => {
        window.removeEventListener('keydown', handleEsc);
        globalOpenModalsCount = Math.max(0, globalOpenModalsCount - 1);
        if (globalOpenModalsCount === 0) {
          document.body.style.overflow = '';
        }
      };
    }
  }, [open, onClose, preventClose, onAttemptClose]);

  if (!open) return null;

  // Cálculo de z-index dinâmico baseado em depth
  const overlayZIndex = depth ? 1050 + depth * 50 : undefined;

  const sizeClass = size && size !== 'default' ? `modal-${size}` : '';
  const isFloatingHeader = !title && !subtitle;

  return (
    <div
      className="modal-overlay"
      style={overlayZIndex ? { zIndex: overlayZIndex } : undefined}
      onClick={() => {
        if (closeOnOverlayClick) handleCloseAttempt();
      }}
    >
      <div
        className={['modal-content', sizeClass, contentClassName || ''].filter(Boolean).join(' ')}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : 'Diálogo'}
        {...(dialogId ? { 'data-dialog-id': dialogId } : {})}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || subtitle || headerExtra || !hideCloseButton) && (
          <div className={`modal-header${isFloatingHeader ? ' modal-header--floating' : ''}`}>
            {(title || subtitle) && (
              <div className="modal-header-titles">
                {title && (typeof title === 'string' ? <h2>{title}</h2> : title)}
                {subtitle && (typeof subtitle === 'string' ? <p className="modal-subtitle">{subtitle}</p> : subtitle)}
              </div>
            )}
            {headerExtra && <div className="modal-header-extra">{headerExtra}</div>}
            {!hideCloseButton && (
              <button
                type="button"
                className="modal-close"
                onClick={handleCloseAttempt}
                aria-label="Fechar diálogo"
              >
                <X size={18} />
              </button>
            )}
          </div>
        )}
        <div
          className={[
            'modal-body',
            isFloatingHeader || flushBody ? 'modal-body--flush' : '',
            noBodyScroll ? 'modal-body--no-scroll' : '',
          ].filter(Boolean).join(' ')}
        >
          {children}
        </div>
        {footer && (
          <div className="modal-footer">
            {footerLeft ? <div className="modal-footer-left">{footerLeft}</div> : null}
            <div className="modal-footer-actions">{footer}</div>
          </div>
        )}
      </div>
    </div>
  );
}
